import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { AuthCallback } from "@/components/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import { BookOpen } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PendingApproval from "@/pages/PendingApproval";
import Dashboard from "@/pages/Dashboard";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import Profile from "@/pages/Profile";
import ReadingLists from "@/pages/ReadingLists";
import ReadingListDetail from "@/pages/ReadingListDetail";
import Leaderboards from "@/pages/Leaderboards";
import GlossaryPage from "@/pages/GlossaryPage";
import Suggestions from "@/pages/Suggestions";
import Rozetler from "@/pages/Rozetler";
import PublicProfile from "@/pages/PublicProfile";
import SavedPosts from "@/pages/SavedPosts";
import { ShareMenu } from "@/pages/Dashboard";
import { BookOpen as BookIcon, Home, Trophy, User, Plus as PlusIcon } from "lucide-react";
import Admin from "@/pages/Admin";
import Messages from "@/pages/Messages";
import WisdomStory from "@/pages/WisdomStory";
import Notifications from "@/pages/Notifications";
import Events from "@/pages/Events";
import Explore from "@/pages/Explore";
import Author from "@/pages/Author";
import { LeftSidebar } from "@/components/LeftSidebar";
import { RightSidebar } from "@/components/RightSidebar";
import { WelcomeModal } from "@/components/WelcomeModal";

function Loading() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page">
      <BookOpen className="text-terra animate-pulse" size={38} />
      <p className="mt-4 font-serif text-2xl text-ink">AktifOkur</p>
    </div>
  );
}

function MobileNav() {
  const location = useLocation();
  const left = [{ to: "/panel", icon: Home, label: "Anasayfa" }, { to: "/kitaplar", icon: BookIcon, label: "Kütüphane" }];
  const right = [{ to: "/lider-tablosu", icon: Trophy, label: "Liderlik" }, { to: "/profil", icon: User, label: "Profilim" }];
  const Item = ({ to, icon: Icon, label }) => (
    <Link to={to} className={`flex-1 flex flex-col items-center gap-0.5 py-2 ${location.pathname === to ? "text-terra" : "text-muted-foreground"}`}>
      <Icon size={20} /><span className="text-[10px]">{label}</span>
    </Link>
  );
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-page/90 backdrop-blur-xl border-t border-line flex items-stretch" data-testid="mobile-nav">
      {left.map((i) => <Item key={i.to} {...i} />)}
      <div className="flex-1 flex items-center justify-center">
        <ShareMenu>
          <button data-testid="mobile-share" className="w-12 h-12 -mt-5 rounded-full bg-terra text-white flex items-center justify-center shadow-lg border-4 border-page">
            <PlusIcon size={22} />
          </button>
        </ShareMenu>
      </div>
      {right.map((i) => <Item key={i.to} {...i} />)}
    </nav>
  );
}

function Shell({ children, bare }) {
  if (bare) return <><Navbar /><main className="min-h-[calc(100vh-4rem)] pb-20 md:pb-0">{children}</main><MobileNav /><WelcomeModal /></>;
  return (
    <>
      <Navbar />
      <div className="min-h-[calc(100vh-4rem)] pb-20 md:pb-0">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6 grid xl:grid-cols-[220px_1fr_290px] gap-6">
          <LeftSidebar />
          <main className="min-w-0">{children}</main>
          <RightSidebar />
        </div>
      </div>
      <MobileNav />
      <WelcomeModal />
    </>
  );
}

function Protected({ children, staff, admin, bare }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user === null) return <Navigate to="/giris" replace />;
  if (user.status !== "approved") return <PendingApproval />;
  if (admin && user.role !== "admin") return <Navigate to="/panel" replace />;
  if (staff && !["admin", "moderator"].includes(user.role)) return <Navigate to="/panel" replace />;
  return <Shell bare={bare}>{children}</Shell>;
}

function Public({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user && user.status === "approved") return <Navigate to="/panel" replace />;
  return children;
}

function RootRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Public><Landing /></Public>} />
      <Route path="/giris" element={<Public><Login /></Public>} />
      <Route path="/kayit" element={<Public><Register /></Public>} />
      <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
      <Route path="/sifre-sifirla" element={<ResetPassword />} />
      <Route path="/panel" element={<Protected><Dashboard /></Protected>} />
      <Route path="/kitaplar" element={<Protected><Books /></Protected>} />
      <Route path="/kesfet" element={<Protected><Explore /></Protected>} />
      <Route path="/kitap/:id" element={<Protected><BookDetail /></Protected>} />
      <Route path="/yazar/:name" element={<Protected><Author /></Protected>} />
      <Route path="/profil" element={<Protected><Profile /></Protected>} />
      <Route path="/listeler" element={<Protected><ReadingLists /></Protected>} />
      <Route path="/liste/:id" element={<Protected><ReadingListDetail /></Protected>} />
      <Route path="/lider-tablosu" element={<Protected><Leaderboards /></Protected>} />
      <Route path="/sozluk" element={<Protected><GlossaryPage /></Protected>} />
      <Route path="/onerilerim" element={<Protected><Suggestions /></Protected>} />
      <Route path="/rozetler" element={<Protected><Rozetler /></Protected>} />
      <Route path="/uye/:id" element={<Protected><PublicProfile /></Protected>} />
      <Route path="/kaydedilenler" element={<Protected><SavedPosts /></Protected>} />
      <Route path="/mesajlar" element={<Protected><Messages /></Protected>} />
      <Route path="/bildirimler" element={<Protected><Notifications /></Protected>} />
      <Route path="/etkinlikler" element={<Protected><Events /></Protected>} />
      <Route path="/hikaye/:slug" element={<Protected><WisdomStory /></Protected>} />
      <Route path="/admin" element={<Protected staff bare><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RootRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
